<template>
  <div class="trips-section">
    <TopBar>
      <template v-slot:left>
        <button class="btn-report-special" @click="createReport" :disabled="isComponentBusy">
          <span v-if="!isCreatingReport">
            <i class="fa fa-download"></i> Export report
          </span>
          <span v-if="isCreatingReport">
            <i class="fa fa-spinner fa-spin"></i> Please wait
          </span>
        </button>

        <button :class="{ 'danger-btn': isShowingTripsTraveled }" v-if="isAttributionModel"
          @click="toggleTripsTraveled(true)">Show trips traveled
        </button>

        <button :class="{ 'danger-btn': !isShowingTripsTraveled }" v-if="isAttributionModel"
          @click="toggleTripsTraveled(false)">Show trips booked
        </button>

      </template>
      <template v-slot:right>
        <MonthSelector />
      </template>
    </TopBar>
    <div class="card">
      <div class="card-header">
        <i class="fa fa-spinner fa-spin card-header--icon" v-if="waitForListFill"></i>
        <i class="fa fa-map card-header--icon" v-else></i>
        <span class="card-header--title">
          {{ isShowingTripsTraveled ? 'Trips Traveled' : 'Trips Booked' }}
        </span>
      </div>

      <div class="card-content">
        <PaginatedTableComponent :maxPage="maxPage" :sub-title="dater.activeMonthStr" :data="trips.data"
          :columns="trips.columns" :headings="trips.headings" :currentPage="currentPage" :isLoading="waitForListFill"
          @changePage="changePage" />
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import Ports from '@/repositories/ports';
import Companies from '@/repositories/companies';
import Dater from '@/repositories/dater'
import MonthSelector from '@/components/shared/MonthSelector.vue'
import PaginatedTableComponent from '@/components/shared/PaginatedTableComponent.vue'
import Api from '@/services/ApiGateway'
import { mapGetters, mapState } from 'vuex';
import TopBar from '../components/shared/TopBar.vue';

const filterTrips = (trips) => {
  return trips.map(trip => ({
    ...trip,
    origin: Ports.location(trip.origin) && Ports.location(trip.origin).alias || trip.origin,
    destination: Ports.location(trip.destination) && Ports.location(trip.destination).alias || trip.destination,
    company: Companies.company(trip.company),
    departureDate: trip.departureDate ? dayjs(trip.departureDate).format('DD/MM/YYYY') : undefined,
    bookedDate: trip.createdAt ? dayjs(trip.createdAt).format('DD/MM/YYYY') : undefined,
  }))
};

export default {
  components: {
    PaginatedTableComponent,
    MonthSelector,
    TopBar
  },
  data() {
    return {
      currentPage: 1,
      dater: Dater,
      tripsTraveledList: [],
      tripsBookedList: [],
      isCreatingReport: false,
      paginationSize: window.ferryhopper && window.ferryhopper.pagination_size || 20,
      tableStructure: [],
      isShowingTripsTraveled: true,
      waitForListFill: false,
    }
  },
  watch: {
    // Executes instantly once component comes into view, and each time the date changes
    getActiveDates(previous, current) {
      this.getTripsList(1);
    }
  },
  computed: {
    ...mapState({
      tripsBookedCount: state => state.statisticsModule.tripsBookedCount,
      tripsTraveledCount: state => state.statisticsModule.tripsTraveledCount,
      model: state => state.statisticsModule.model,
    }),
    ...mapGetters({
      getActiveDates: 'datesModule/getActiveDates',
      impersonateUser: 'authentication/impersonateUser',
    }),
    isAttributionModel() {
      return this.model === 'TRAVEL_DATE_ATTRIBUTION'
    },
    tripsList() {
      return (this.isAttributionModel && this.isShowingTripsTraveled) ? this.tripsTraveledList : this.tripsBookedList;
    },
    trips() {
      const headings = this.tableStructure.map(c => c.columnName);
      const columns = this.tableStructure.map(c => c.itemProperty);
      return {
        headings: [...headings],
        columns: [...columns],
        data: [...this.tripsList],
      }
    },
    isComponentBusy() {
      return this.isCreatingReport || this.waitForListFill
    },
    maxPage() {
      const totalRowsCount = (this.isAttributionModel && this.isShowingTripsTraveled) ? this.tripsTraveledCount : this.tripsBookedCount;
      return Math.ceil(totalRowsCount / this.paginationSize)
    }
  },
  methods: {
    createReport() {
      this.isCreatingReport = true
      const tripType = this.isShowingTripsTraveled ? 'traveled' : 'booked';
      Api.userReport('trips', this.impersonateUser, tripType).then(res => {
        window.open(res.data.url, 'name')
      }).finally(() => this.isCreatingReport = false);
    },
    changePage(page) {
      this.currentPage = page;
      this.getTripsList(this.currentPage);
    },
    toggleTripsTraveled(value) {
      this.isShowingTripsTraveled = value;
      this.changePage(1);
    },
    getTripsList(page) {
      this.waitForListFill = true;
      Api.fetchTripsTablePage(page, this.impersonateUser).then(result => {
        const { model, tripsTraveledCount, tripsTraveled, tripsBooked, tripsBookedCount, tableStructure } = result.data;

        this.tableStructure = tableStructure;
        this.$store.dispatch('statisticsModule/setModel', model);

        this.$store.dispatch('statisticsModule/setTripsTraveledCount', tripsTraveledCount || 0);

        // Only in the case of TRAVEL_DATE_ATTRIBUTION model we can "trust" the tripsBookedCount value
        // In the other cases, this number always equals to the pagination
        if (model === 'TRAVEL_DATE_ATTRIBUTION') {
          this.$store.dispatch('statisticsModule/setTripsBookedCount', tripsBookedCount);
        }

        this.tripsTraveledList = filterTrips(tripsTraveled || []);
        this.tripsBookedList = filterTrips(tripsBooked || []);
        if (model !== 'TRAVEL_DATE_ATTRIBUTION') {
          this.isShowingTripsTraveled = false;
        }
        this.waitForListFill = false;
      }).catch(error => this.waitForListFill = false)
    }
  },
}
</script>

<style scoped>
.trips-section {
  padding: 1rem 0 2rem 0;
}
</style>
